import { z } from 'zod';
import { ContentReview, ReviewState } from './content-review';
import { RenewalReading } from './renewal';

export const Track = z.enum(['core', 'regional', 'operator', 'foundation']);
export type Track = z.infer<typeof Track>;
export const Region = z.enum(['antarctica', 'arctic']);
export type Region = z.infer<typeof Region>;
export const Kind = z.enum(['brief', 'module', 'film', 'certification']);
export type Kind = z.infer<typeof Kind>;
export const Classification = z.enum(['evergreen', 'volatile', 'script']);

export const Block = z.union([
  z.tuple([z.literal('p'), z.string()]),
  z.tuple([z.literal('cue'), z.string()]),
  z.tuple([z.literal('onscreen'), z.string()]),
  z.tuple([z.literal('music'), z.string()]),
  z.tuple([z.literal('beat')]),
]);
export type Block = z.infer<typeof Block>;
export const InlineSchema = z.object({ t: z.enum(['text', 'b', 'i']), v: z.string() });

export const SupportEmail = z.string().trim().email().max(254);

export const TenantPublic = z.object({
  slug: z.string(),
  name: z.string(),
  short_name: z.string(),
  support_email: SupportEmail.nullable().optional(),
  brand_mode: z.enum(['endorsed', 'whitelabel']),
  accent_light: z.string(),
  accent_dark: z.string(),
  logo_url: z.string().nullable(),
  display_font: z.enum(['instrument-sans', 'inter']),
  credential_name: z.string(),
  operator_slug: z.string().nullable(),
  enrolment_mode: z.enum(['invite', 'code']),
  signatory_name: z.string().nullable(),
  signatory_title: z.string().nullable(),
});
export type TenantPublic = z.infer<typeof TenantPublic>;

/**
 * `authentication` and `programme` are present only while `ACCOUNT_SESSIONS=accounts`, so their
 * presence is how the client knows the account layer is on (`isAccountMode`). With the switch off
 * the payload is the three fields it has always carried, byte for byte.
 */
export const SessionState = z.object({
  authenticated: z.boolean(),
  user: z
    .object({ id: z.string(), email: z.string(), name: z.string().nullable(), is_superadmin: z.boolean() })
    .nullable(),
  membership: z
    .object({
      role: z.enum(['learner', 'operator_admin']),
      status: z.enum(['invited', 'active', 'removed']),
      virtual: z.boolean(),
    })
    .nullable(),
  authentication: z
    .object({
      at: z.string(),
      method: z.enum(['email_link', 'legacy_session', 'provider']),
      expires_at: z.string(),
      /** Authenticated within the last 24 hours: the canon's limit for a sensitive change. */
      fresh: z.boolean(),
    })
    .nullable()
    .optional(),
  programme: z
    .object({
      slug: z.string(),
      name: z.string(),
      access: z.enum(['none', 'invited', 'active', 'removed']),
    })
    .optional(),
});
export type SessionState = z.infer<typeof SessionState>;

/** The account layer is on when the server sent the programme it selected for this request. */
export const isAccountMode = (session: SessionState | undefined): boolean => session?.programme !== undefined;

/** Whether this person may open this programme's material right now. */
export const hasProgrammeAccess = (session: SessionState | undefined): boolean =>
  !!(
    session?.authenticated &&
    session.user &&
    (isAccountMode(session)
      ? session.programme?.access === 'active'
      : session.user.is_superadmin || ['active', 'invited'].includes(session.membership?.status ?? ''))
  );
export const AuthRequestBody = z.object({
  email: z.string().email().max(254),
  next: z.string().startsWith('/').max(512).optional(),
  enrol_code: z.string().max(64).optional(),
});

export const Progress = z.object({
  status: z.enum(['started', 'completed']),
  position_s: z.number().int().nonnegative(),
  chapter_index: z.number().int().nonnegative(),
  completed_at: z.string().nullable(),
});
export const BestAttempt = z.object({
  score: z.number().int(),
  total: z.number().int(),
  passed: z.boolean(),
  submitted_at: z.string(),
});
export const Chapter = z.object({
  index: z.number().int(),
  title: z.string(),
  start_s: z.number().int().nullable(),
  block_index: z.number().int().nonnegative().nullable().default(null),
});
export type Chapter = z.infer<typeof Chapter>;
/**
 * A chapter as the lists carry it: its place, its title and, on a timed item, its start. What a
 * module's progress bar is segmented by and what the audio queue lists, without the script.
 */
export const ChapterSummary = Chapter.pick({ index: true, title: true, start_s: true });
export type ChapterSummary = z.infer<typeof ChapterSummary>;
export const LibraryItem = z.object({
  id: z.string(),
  kind: Kind,
  title: z.string(),
  head: z.string().nullable(),
  tier: z.enum(['foundation', 'specialist']),
  track: Track,
  region: Region.nullable(),
  operator_slug: z.string().nullable(),
  classification: Classification,
  runtime_label: z.string(),
  words: z.number().int().nullable(),
  source_label: z.string().nullable(),
  module_num: z.string().nullable(),
  sort_order: z.number().int(),
  card_id: z.string().nullable(),
  has_audio: z.boolean(),
  question_count: z.number().int(),
  progress: Progress.nullable(),
  best_attempt: BestAttempt.nullable(),
  /** In reading order. Older servers sent none, so an absent list reads as empty. */
  chapters: z.array(ChapterSummary).default([]),
});
export type LibraryItem = z.infer<typeof LibraryItem>;

export const ItemBody = z.object({
  chapters: z.array(Chapter),
  blocks: z.array(Block),
  panels: z.array(z.string()),
  cheat_sheet: z.array(z.object({ heading: z.string(), bullets: z.array(z.array(InlineSchema)) })),
  talking_points: z.array(z.string()),
  production: z
    .object({
      running_time: z.string().nullable(),
      audience: z.string().nullable(),
      learning_promise: z.string().nullable(),
      outcomes: z.array(z.string()),
      key_facts: z.array(z.string()),
    })
    .nullable(),
  audio_url: z.string().nullable(),
  audio_duration_s: z.number().int().nullable(),
});
export const ItemDetail = LibraryItem.extend({ body: ItemBody, check_count: z.number().int() });
export type ItemDetail = z.infer<typeof ItemDetail>;
export const ProgressPut = z.object({
  position_s: z.number().int().nonnegative().max(86400),
  chapter_index: z.number().int().nonnegative().max(200),
  status: z.enum(['started', 'completed']).optional(),
});

export const AttemptCreateBody = z.object({ item_id: z.string(), kind: z.enum(['check', 'quiz']) });
export const ServedQuestion = z.object({
  id: z.string(),
  stem: z.string(),
  options: z.array(z.string()).length(4),
  is_scenario: z.boolean(),
});
export const AttemptCreated = z.object({
  renewal_update: RenewalReading.optional(),
  attempt_id: z.string(),
  item_id: z.string(),
  kind: z.enum(['check', 'quiz', 'recert']),
  pass_mark: z.number().int(),
  questions: z.array(ServedQuestion),
});
export const AttemptSubmitBody = z.object({
  answers: z
    .array(z.object({ question_id: z.string(), chosen_index: z.number().int().min(0).max(3) }))
    .min(1)
    .max(30),
});
export const CredentialSummary = z.object({
  code: z.string(),
  tier: z.enum(['foundation', 'specialist']),
  region: Region.nullable(),
  title: z.string(),
  endorsed_by: z.string().nullable(),
  issued_at: z.string(),
  valid_until: z.string(),
  recertified_at: z.string().nullable(),
  status: z.enum(['current', 'expiring', 'expired', 'revoked']),
});
export const AttemptResult = z.object({
  attempt_id: z.string(),
  score: z.number().int(),
  total: z.number().int(),
  passed: z.boolean(),
  pass_mark: z.number().int(),
  results: z.array(
    z.object({
      question_id: z.string(),
      chosen_index: z.number().int(),
      correct_index: z.number().int(),
      rationale: z.string(),
    }),
  ),
  unlocked_card_id: z.string().nullable(),
  credentials_issued: z.array(CredentialSummary),
});
export type AttemptResult = z.infer<typeof AttemptResult>;

export const CardLive = z.object({
  review: ContentReview.nullable().default(null),
  review_state: ReviewState.default('unreviewed'),
  field: z.string(),
  source: z.string(),
  slot_key: z.string(),
  value: z.string().nullable(),
  published_at: z.string().nullable(),
});
export const Card = z.object({
  id: z.string(),
  item_id: z.string(),
  title: z.string(),
  tier_label: z.string(),
  tag: z.enum(['EVERGREEN', 'VOLATILE']),
  source_label: z.string(),
  facts: z.array(z.array(InlineSchema)),
  say: z.string(),
  live: CardLive.nullable(),
  locked: z.boolean(),
  saved: z.boolean(),
  sort_order: z.number().int(),
});
export type Card = z.infer<typeof Card>;
export const CardList = z.object({
  data: z.array(Card),
  tiers: z.array(z.string()),
  recent_ids: z.array(z.string()).max(5).default([]),
});

export const CredentialPolicy = z.object({
  version: z.literal(1),
  prerequisite: z.literal('foundation-current-at-award'),
  issued: z
    .object({
      at: z.string(),
      modules: z.array(
        z.object({
          id: z.string(),
          title: z.string(),
          pass_mark: z.number().int(),
          attempt_id: z.string(),
          assessment_version: z.string().nullable(),
        }),
      ),
      foundation: z.object({ code: z.string(), valid_until: z.string() }).nullable(),
    })
    .nullable(),
  renewed: z
    .object({
      at: z.string(),
      pass_mark: z.number().int(),
      attempt_id: z.string(),
      assessment_version: z.string(),
      update: z.object({ id: z.string(), title: z.string() }).optional(),
    })
    .nullable(),
});
export type CredentialPolicy = z.infer<typeof CredentialPolicy>;

/** A credential's unguessable public handle: exactly 128 random bits rendered as lower-case hex. */
export const PublicShareId = z.string().regex(/^[0-9a-f]{32}$/);

/** Only the issuer presentation fields a public credential page needs. */
export const CredentialIssuer = TenantPublic.pick({
  name: true,
  brand_mode: true,
  accent_light: true,
  accent_dark: true,
  logo_url: true,
  display_font: true,
  credential_name: true,
});
export type CredentialIssuer = z.infer<typeof CredentialIssuer>;

export const Credential = CredentialSummary.extend({
  public_share_id: PublicShareId,
  policy: CredentialPolicy.nullable(),
  holder_name: z.string().nullable(),
  tenant_name: z.string(),
  verify_url: z.string(),
  badge_svg_url: z.string(),
  badge_json_url: z.string(),
  certificate_url: z.string(),
  composite_score: z.number().int().nullable(),
});
export type Credential = z.infer<typeof Credential>;
export const CredentialList = z.object({
  data: z.array(Credential),
  status: z.enum(['none', 'in_progress', 'current', 'expiring', 'expired']),
  remaining: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      track: Track,
      head: z.string().nullable(),
      estimated_minutes: z.number().int().positive(),
    }),
  ),
  recert_available: z.boolean(),
});
export type CredentialList = z.infer<typeof CredentialList>;
export const VerifyResult = z.object({
  found: z.boolean(),
  record: z
    .object({
      code: z.string(),
      holder_name: z.string().nullable(),
      title: z.string(),
      tier: z.string(),
      region: Region.nullable(),
      endorsed_by: z.string().nullable(),
      issued_at: z.string(),
      recertified_at: z.string().nullable(),
      valid_until: z.string(),
      status: z.enum(['current', 'expiring', 'expired', 'revoked']),
      issuer: CredentialIssuer,
      criteria: z.array(z.string()),
    })
    .nullable(),
});

export const Me = z.object({
  user: z.object({
    id: z.string(),
    email: z.string(),
    name: z.string().nullable(),
    photo_url: z.string().nullable().default(null),
    is_superadmin: z.boolean(),
  }),
  membership: z.object({
    role: z.enum(['learner', 'operator_admin']),
    status: z.enum(['invited', 'active', 'removed']),
    joined_at: z.string().nullable(),
    virtual: z.boolean(),
  }),
  tenant: TenantPublic,
  progress_summary: z.object({
    core_total: z.number().int(),
    core_passed: z.number().int(),
    regional_total: z.number().int(),
    regional_passed: z.number().int(),
    operator_total: z.number().int(),
    operator_passed: z.number().int(),
    briefs_total: z.number().int(),
    briefs_completed: z.number().int(),
  }),
  continue_item: LibraryItem.nullable(),
  next_brief: LibraryItem.nullable(),
  saved_count: z.number().int(),
  credential_status: z.enum(['none', 'in_progress', 'current', 'expiring', 'expired']),
});
export type Me = z.infer<typeof Me>;
export const MePatch = z.object({ name: z.string().trim().min(1).max(120) });

export const SponsorSummary = z.object({
  as_of: z.string(),
  seats: z.number().int(),
  started: z.number().int(),
  core_complete: z.number().int(),
  certified: z.number().int(),
  // Today's seated population and currently valid Foundation holders, accumulated by original issue month.
  monthly: z.array(z.object({ month: z.string(), pct: z.number() })),
  modules: z.array(z.object({ id: z.string(), title: z.string(), completion_pct: z.number() })),
  funnel: z.array(z.object({ stage: z.string(), n: z.number().int() })),
  median_days_to_competency: z.number().nullable(),
  card_opens: z.number().int(),
  brief_completions: z.number().int(),
  quiz_first_pass_pct: z.number().nullable(),
});
export type SponsorSummary = z.infer<typeof SponsorSummary>;
export const LearnerRow = z.object({
  user_id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  status: z.enum(['invited', 'active', 'removed']),
  role: z.enum(['learner', 'operator_admin']),
  joined_at: z.string().nullable(),
  last_seen_at: z.string().nullable(),
  started: z.boolean(),
  modules_passed: z.number().int(),
  briefs_completed: z.number().int(),
  credential: CredentialSummary.nullable(),
});
export const LearnerDetail = LearnerRow.extend({
  items: z.array(LibraryItem),
  attempts: z.array(
    z.object({
      id: z.string(),
      item_id: z.string(),
      kind: z.string(),
      score: z.number().int(),
      total: z.number().int(),
      passed: z.boolean(),
      submitted_at: z.string().nullable(),
    }),
  ),
});
// Five new addresses measured below 50 D1 queries, including authentication and idempotency.
export const INVITE_BATCH_SIZE = 5;
export const INVITE_COHORT_MAX = 500;
export const InviteBody = z.object({
  emails: z.array(z.string().email()).min(1).max(INVITE_BATCH_SIZE),
  role: z.enum(['learner', 'operator_admin']).default('learner'),
});
export const InviteResult = z.object({
  invited: z.array(z.string()),
  already_members: z.array(z.string()),
  failed: z.array(z.string()),
});
export const LOGO_MAX_BYTES = 200 * 1024;
export const LOGO_MAX_SIDE = 1024;

export const ConfigRevision = z.object({ revision: z.uuid().nullable() });

export const Brand = z.object({
  revision: ConfigRevision.shape.revision,
  logo_needs_png: z.boolean(),
  name: z.string(),
  short_name: z.string(),
  accent_light: z.string(),
  accent_dark: z.string(),
  effective_accent_light: z.string(),
  effective_accent_dark: z.string(),
  logo_url: z.string().nullable(),
  display_font: z.enum(['instrument-sans', 'inter']),
  signatory_name: z.string().nullable(),
  signatory_title: z.string().nullable(),
  brand_mode: z.enum(['endorsed', 'whitelabel']),
  hostname: z.string().nullable(),
});
export const BrandPut = z.object({
  revision: ConfigRevision.shape.revision,
  name: z.string().trim().min(2).max(80),
  short_name: z.string().trim().min(1).max(24),
  accent_light: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  accent_dark: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  display_font: z.enum(['instrument-sans', 'inter']),
  signatory_name: z.string().trim().max(120).nullable(),
  signatory_title: z.string().trim().max(120).nullable(),
});
export const BrandPutResult = z.object({ brand: Brand, notes: z.array(z.string()) });
export const SlotRevision = z.object({ revision: z.uuid().nullable() });
export const Slot = z.object({
  revision: SlotRevision.shape.revision,
  draft_review: ContentReview.nullable().default(null),
  published_review: ContentReview.nullable().default(null),
  review_state: ReviewState.default('unreviewed'),
  slot_key: z.string(),
  cards: z.array(z.object({ id: z.string(), title: z.string(), field: z.string() })),
  draft_value: z.string().nullable(),
  draft_at: z.string().nullable(),
  draft_by: z.string().nullable(),
  published_value: z.string().nullable(),
  published_at: z.string().nullable(),
  published_by: z.string().nullable(),
  dirty: z.boolean(),
});
export const SlotPut = z.object({
  revision: SlotRevision.shape.revision,
  value: z.string().max(2000).nullable(),
  review: ContentReview.nullable().default(null),
});
export const SlotHistoryRow = z.object({
  before_review: ContentReview.nullable().default(null),
  after_review: ContentReview.nullable().default(null),
  action: z.enum(['draft', 'publish', 'unpublish']),
  actor: z.string(),
  before: z.string().nullable(),
  after: z.string().nullable(),
  at: z.string(),
});
export const SponsorSettings = z.object({
  revision: ConfigRevision.shape.revision,
  // Older clients may omit this field; an omitted setting must never clear a saved inbox.
  support_email: SupportEmail.nullable().optional(),
  enrolment_mode: z.enum(['invite', 'code']),
  enrol_code: z.string().nullable(),
  operator_pass_mark: z.number().int().min(50).max(100).nullable(),
});
export const TenantAdmin = TenantPublic.extend({
  revision: ConfigRevision.shape.revision,
  id: z.string(),
  hostname: z.string().nullable(),
  status: z.enum(['active', 'disabled']),
  operator_pass_mark: z.number().int().nullable(),
  signatory_name: z.string().nullable(),
  signatory_title: z.string().nullable(),
  created_at: z.string(),
  seats: z.number().int(),
  certified: z.number().int(),
});
export const TenantCreate = z.object({
  slug: z.string().regex(/^[a-z0-9-]{2,32}$/),
  name: z.string().trim().min(2).max(80),
  short_name: z.string().trim().min(1).max(24),
  hostname: z
    .string()
    .regex(/^[a-z0-9.-]+$/)
    .nullable(),
  brand_mode: z.enum(['endorsed', 'whitelabel']).default('endorsed'),
  operator_slug: z
    .string()
    .regex(/^[a-z0-9-]{1,32}$/)
    .nullable(),
  credential_name: z.string().trim().min(2).max(80).default('Expedition Cruise Specialist'),
});
export const TenantPatch = TenantCreate.partial().extend({
  // Creation defaults must never turn an omitted PATCH field into a write.
  brand_mode: TenantCreate.shape.brand_mode.removeDefault().optional(),
  credential_name: TenantCreate.shape.credential_name.removeDefault().optional(),
  revision: ConfigRevision.shape.revision,
  status: z.enum(['active', 'disabled']).optional(),
});
export const RegisterRow = CredentialSummary.extend({
  holder_name: z.string().nullable(),
  holder_email: z.string(),
  tenant_slug: z.string(),
  tenant_name: z.string(),
  revoked_at: z.string().nullable(),
  revoke_reason: z.string().nullable(),
});
export const RevokeBody = z.object({ reason: z.string().trim().min(3).max(500) });
export const AudioState = z.object({
  version: z.string().nullable(),
  duration_s: z.number().nullable(),
  revision: z.uuid().nullable(),
  can_undo: z.boolean(),
  previous_version: z.string().nullable(),
});
export const AudioRevision = z.object({ revision: z.uuid().nullable() });
export const ContentSummary = z.object({
  content_version: z.string().nullable(),
  counts: z.record(z.string(), z.number().int()),
  items: z.array(
    z.object({
      id: z.string(),
      kind: Kind,
      title: z.string(),
      has_audio: z.boolean(),
      question_count: z.number().int(),
    }),
  ),
});
export const AdminUser = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  is_superadmin: z.boolean(),
  memberships: z.array(z.object({ tenant_slug: z.string(), role: z.string(), status: z.string() })),
});
export const AdminUserPatch = z.object({ user_id: z.string(), is_superadmin: z.boolean() });

export const Problem = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  trace_id: z.string(),
});
export type Problem = z.infer<typeof Problem>;
export function Page<T extends z.ZodTypeAny>(item: T) {
  return z.object({ data: z.array(item), next_cursor: z.string().nullable(), has_more: z.boolean() });
}
